import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  Build as BuildIcon,
  Construction as ConstructionIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Delete as DeleteIcon,
  Draw as DrawIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { rapportiniService, clientiService, cantieriService, usersService } from '../services/api';

// Componente sezione form riutilizzabile
const FormSection = ({ icon, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const RapportinoStandaloneModal = ({ open, onClose, onSuccess }) => {
  const currentUser = useSelector((state) => state.auth.user);

  // Informazioni generali
  const [data, setData] = useState(dayjs());
  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [tipologia, setTipologia] = useState('');
  const [loadingClienti, setLoadingClienti] = useState(false);
  const [loadingCantieri, setLoadingCantieri] = useState(false);

  // Orari e tempi
  const [oraPartenzaSede, setOraPartenzaSede] = useState('');
  const [oraArrivoCantiere, setOraArrivoCantiere] = useState('');
  const [oraInizioLavoro, setOraInizioLavoro] = useState('');
  const [oraFineLavoro, setOraFineLavoro] = useState('');
  const [oraPartenzaCantiere, setOraPartenzaCantiere] = useState('');
  const [oraArrivoSede, setOraArrivoSede] = useState('');
  const [kmPercorsi, setKmPercorsi] = useState('');
  const [tempoViaggioAndata, setTempoViaggioAndata] = useState('');
  const [tempoViaggioRitorno, setTempoViaggioRitorno] = useState('');

  // Task
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskUpdates, setTaskUpdates] = useState({});
  const [searchTask, setSearchTask] = useState('');

  // Campi testo
  const [descrizioneIntervento, setDescrizioneIntervento] = useState('');
  const [materialiUtilizzati, setMaterialiUtilizzati] = useState('');
  const [note, setNote] = useState('');

  // Firma
  const [nomeFirmatario, setNomeFirmatario] = useState('');
  const [responsabileAssente, setResponsabileAssente] = useState(false);
  const sigCanvas = useRef(null);

  // Allegati
  const [allegati, setAllegati] = useState([]);
  const fileInputRef = useRef(null);

  // Operatori (tecnici che hanno lavorato) e relative ore
  const [utentiDisponibili, setUtentiDisponibili] = useState([]);
  const [operatori, setOperatori] = useState([]);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Carica clienti all'apertura
  useEffect(() => {
    if (open) {
      loadClienti();
      loadUtenti();
      resetForm();
    }
  }, [open]);

  // Carica cantieri quando si seleziona un cliente
  useEffect(() => {
    if (selectedCliente) {
      loadCantieri();
    } else {
      setCantieri([]);
      setSelectedCantiere(null);
    }
  }, [selectedCliente]);

  // Carica task quando si seleziona un cantiere
  useEffect(() => {
    if (selectedCantiere) {
      loadTasks();
    } else {
      setTasks([]);
      setTaskUpdates({});
    }
  }, [selectedCantiere]);

  const resetForm = () => {
    setData(dayjs());
    setSelectedCliente(null);
    setSelectedCantiere(null);
    setTipologia('');
    setOraPartenzaSede('');
    setOraArrivoCantiere('');
    setOraInizioLavoro('');
    setOraFineLavoro('');
    setOraPartenzaCantiere('');
    setOraArrivoSede('');
    setKmPercorsi('');
    setTempoViaggioAndata('');
    setTempoViaggioRitorno('');
    setTasks([]);
    setTaskUpdates({});
    setSearchTask('');
    setDescrizioneIntervento('');
    setMaterialiUtilizzati('');
    setNote('');
    setNomeFirmatario('');
    setResponsabileAssente(false);
    setAllegati([]);
    // Pre-include il compilatore (chi crea il rapportino) tra gli operatori
    setOperatori(
      currentUser
        ? [{ user_id: currentUser.id, nome: `${currentUser.nome} ${currentUser.cognome}`, ore: 0 }]
        : []
    );
    setError('');
  };

  const loadUtenti = async () => {
    try {
      const [tecRes, capoRes] = await Promise.all([
        usersService.getTecnici(),
        usersService.getCapocantieri(),
      ]);
      const merge = {};
      [
        ...(Array.isArray(tecRes.data) ? tecRes.data : []),
        ...(Array.isArray(capoRes.data) ? capoRes.data : []),
      ].forEach((u) => {
        merge[u.id] = { id: u.id, nome: `${u.nome} ${u.cognome}` };
      });
      // Assicura che il compilatore sia sempre selezionabile
      if (currentUser) {
        merge[currentUser.id] = { id: currentUser.id, nome: `${currentUser.nome} ${currentUser.cognome}` };
      }
      setUtentiDisponibili(Object.values(merge).sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (err) {
      console.error('Errore caricamento utenti:', err);
    }
  };

  const loadClienti = async () => {
    try {
      setLoadingClienti(true);
      const response = await clientiService.getAll();
      setClienti(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Errore caricamento clienti:', err);
    } finally {
      setLoadingClienti(false);
    }
  };

  const loadCantieri = async () => {
    try {
      setLoadingCantieri(true);
      const response = await cantieriService.getAll();
      const allCantieri = Array.isArray(response.data) ? response.data : [];
      // Filtra per cliente selezionato
      const filtered = allCantieri.filter(c => c.cliente_id === selectedCliente.id);
      setCantieri(filtered);
    } catch (err) {
      console.error('Errore caricamento cantieri:', err);
    } finally {
      setLoadingCantieri(false);
    }
  };

  const loadTasks = async () => {
    if (!selectedCantiere?.id) return;
    try {
      setLoadingTasks(true);
      const response = await cantieriService.getTasks(selectedCantiere.id);
      const tasksData = Array.isArray(response.data.tasks) ? response.data.tasks : [];
      setTasks(tasksData);

      const initialUpdates = {};
      tasksData.forEach(task => {
        initialUpdates[task.id] = {
          id: task.id,
          descrizione: task.descrizione,
          quantita: task.quantita,
          quantita_oggi: 0,
          quantita_completata: task.quantita_completata || 0,
          completata: task.completata || false,
        };
      });
      setTaskUpdates(initialUpdates);
    } catch (err) {
      console.error('Errore caricamento tasks:', err);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleTaskQuantityChange = (taskId, quantitaOggi) => {
    const value = parseFloat(quantitaOggi) || 0;
    const task = tasks.find(t => t.id === taskId);
    const quantitaOriginale = task?.quantita_completata || 0;
    const nuovoTotale = quantitaOriginale + value;

    setTaskUpdates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        quantita_oggi: value,
        quantita_completata: nuovoTotale,
        completata: task && task.quantita > 0 ? nuovoTotale >= task.quantita : false,
      }
    }));
  };

  // Calcolo ore lavorate automatico
  const calcolaOreLavorate = () => {
    if (oraInizioLavoro && oraFineLavoro) {
      const [hI, mI] = oraInizioLavoro.split(':').map(Number);
      const [hF, mF] = oraFineLavoro.split(':').map(Number);
      const minuti = (hF * 60 + mF) - (hI * 60 + mI);
      if (minuti > 0) {
        return (minuti / 60).toFixed(2);
      }
    }
    return '0';
  };

  // Allegati
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAllegati = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 5 * 1024 * 1024) {
        setError(`Il file ${file.name} supera i 5MB`);
        continue;
      }
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onload = (e) => {
          resolve({
            filename: file.name,
            data_base64: e.target.result,
            content_type: file.type,
            size: file.size,
          });
        };
      });
      reader.readAsDataURL(file);
      const allegato = await base64Promise;
      newAllegati.push(allegato);
    }
    setAllegati(prev => [...prev, ...newAllegati]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAllegato = (index) => {
    setAllegati(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType) => {
    return contentType && contentType.startsWith('image/');
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validazione
      if (!selectedCliente) {
        setError('Seleziona un cliente');
        return;
      }
      if (!data) {
        setError('Seleziona una data');
        return;
      }
      if (!tipologia) {
        setError('Seleziona la tipologia');
        return;
      }
      if (tipologia === 'manutenzione' && !descrizioneIntervento.trim()) {
        setError("La descrizione dell'intervento è obbligatoria per le manutenzioni");
        return;
      }
      if (tipologia === 'manutenzione' && !responsabileAssente) {
        if (sigCanvas.current && sigCanvas.current.isEmpty()) {
          setError('La firma del cliente è obbligatoria per le manutenzioni');
          return;
        }
        if (!nomeFirmatario.trim()) {
          setError('Il nome del firmatario è obbligatorio');
          return;
        }
      }

      // Prepara task svolte
      const tasksSvolte = Object.values(taskUpdates).filter(task => task.quantita_oggi > 0);

      const rapportinoData = {
        data: data.format('YYYY-MM-DD'),
        cliente_id: selectedCliente.id,
        cantiere_id: selectedCantiere?.id || null,
        tipologia: tipologia,
        ora_partenza_sede: oraPartenzaSede || null,
        ora_arrivo_cantiere: oraArrivoCantiere || null,
        ora_inizio_lavoro: oraInizioLavoro || null,
        ora_fine_lavoro: oraFineLavoro || null,
        ora_partenza_cantiere: oraPartenzaCantiere || null,
        ora_arrivo_sede: oraArrivoSede || null,
        km_percorsi: kmPercorsi ? parseFloat(kmPercorsi) : 0,
        tempo_viaggio_andata: tempoViaggioAndata ? parseInt(tempoViaggioAndata) : null,
        tempo_viaggio_ritorno: tempoViaggioRitorno ? parseInt(tempoViaggioRitorno) : null,
        descrizione_intervento: descrizioneIntervento || '',
        materiali_utilizzati: materialiUtilizzati || '',
        note: note || '',
        tasks_svolte: tasksSvolte,
        ore_per_tecnico: operatori.map((op) => ({
          user_id: op.user_id,
          nome: op.nome,
          ore: op.ore,
        })),
      };

      // Firma
      if (tipologia === 'manutenzione' && !responsabileAssente && sigCanvas.current && !sigCanvas.current.isEmpty()) {
        rapportinoData.firma_cliente = sigCanvas.current.toDataURL('image/png');
        rapportinoData.nome_firmatario = nomeFirmatario;
      }

      // Allegati
      if (allegati.length > 0) {
        rapportinoData.allegati = allegati;
      }

      await rapportiniService.create(rapportinoData);

      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      console.error('Errore nella creazione del rapportino:', err);
      if (err.isOffline && err.queued) {
        // Salvato nella coda offline: chiudi come un salvataggio riuscito (il toast globale avvisa)
        handleClose();
        return;
      }
      setError(err.isOffline ? err.message : (err.response?.data?.message || 'Errore nella creazione del rapportino'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const filteredTasks = tasks.filter(task =>
    !searchTask || task.descrizione?.toLowerCase().includes(searchTask.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#1a1a1a',
        color: 'white',
        py: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Nuovo Rapportino
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 1. Informazioni Generali */}
        <FormSection icon={<BusinessIcon color="primary" />} title="Informazioni Generali">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DatePicker
              label="Data *"
              value={data}
              onChange={(newVal) => setData(newVal)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { fullWidth: true, size: 'small' }
              }}
            />

            <Autocomplete
              options={clienti}
              getOptionLabel={(option) => option.ragione_sociale || ''}
              value={selectedCliente}
              onChange={(_, newVal) => {
                setSelectedCliente(newVal);
                setSelectedCantiere(null);
              }}
              loading={loadingClienti}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente *"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingClienti ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              options={cantieri}
              getOptionLabel={(option) => option.nome || ''}
              value={selectedCantiere}
              onChange={(_, newVal) => setSelectedCantiere(newVal)}
              loading={loadingCantieri}
              disabled={!selectedCliente}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cantiere"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCantieri ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Tipologia *</InputLabel>
              <Select
                value={tipologia}
                label="Tipologia *"
                onChange={(e) => setTipologia(e.target.value)}
              >
                <MenuItem value="manutenzione">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BuildIcon fontSize="small" /> Manutenzione
                  </Box>
                </MenuItem>
                <MenuItem value="impianto">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ConstructionIcon fontSize="small" /> Impianto
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </FormSection>

        <Divider sx={{ my: 2 }} />

        {/* 2. Orari e Tempi */}
        <FormSection icon={<AccessTimeIcon color="primary" />} title="Orari e Tempi">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Partenza Sede"
              type="time"
              size="small"
              value={oraPartenzaSede}
              onChange={(e) => setOraPartenzaSede(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Arrivo Cantiere / Inizio Lavoro"
              type="time"
              size="small"
              value={oraArrivoCantiere}
              onChange={(e) => { setOraArrivoCantiere(e.target.value); setOraInizioLavoro(e.target.value); }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fine Lavoro"
              type="time"
              size="small"
              value={oraFineLavoro}
              onChange={(e) => setOraFineLavoro(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Partenza Cantiere"
              type="time"
              size="small"
              value={oraPartenzaCantiere}
              onChange={(e) => setOraPartenzaCantiere(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Arrivo Sede"
              type="time"
              size="small"
              value={oraArrivoSede}
              onChange={(e) => setOraArrivoSede(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField
              label="Ore Lavorate"
              size="small"
              value={calcolaOreLavorate()}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Km Percorsi"
              type="number"
              size="small"
              value={kmPercorsi}
              onChange={(e) => setKmPercorsi(e.target.value)}
              fullWidth
            />
            <TextField
              label="Viaggio Andata (min)"
              type="number"
              size="small"
              value={tempoViaggioAndata}
              onChange={(e) => setTempoViaggioAndata(e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField
              label="Viaggio Ritorno (min)"
              type="number"
              size="small"
              value={tempoViaggioRitorno}
              onChange={(e) => setTempoViaggioRitorno(e.target.value)}
              fullWidth
            />
          </Box>
        </FormSection>

        {/* Operatori che hanno lavorato (con ore) */}
        <FormSection icon={<GroupIcon color="primary" />} title="Operatori — Ore Lavorate">
          <Autocomplete
            options={utentiDisponibili.filter((u) => !operatori.some((op) => op.user_id === u.id))}
            getOptionLabel={(o) => o.nome || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={null}
            blurOnSelect
            onChange={(e, newValue) => {
              if (newValue) {
                setOperatori((prev) => [
                  ...prev,
                  { user_id: newValue.id, nome: newValue.nome, ore: calcolaOreLavorate() || 0 },
                ]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Aggiungi operatore"
                placeholder="Cerca un tecnico/capocantiere..."
                size="small"
              />
            )}
            sx={{ mb: 2 }}
          />

          {operatori.length === 0 ? (
            <Alert severity="info">
              Aggiungi gli operatori che hanno lavorato e indica le ore di ciascuno.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Operatore</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Ore Lavorate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Rimuovi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operatori.map((op, idx) => (
                    <TableRow key={op.user_id}>
                      <TableCell>
                        <Chip label={op.nome} color="success" size="small" variant="outlined" />
                        {currentUser && op.user_id === currentUser.id && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (tu)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={op.ore}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setOperatori((prev) =>
                              prev.map((item, i) => (i === idx ? { ...item, ore: val } : item))
                            );
                          }}
                          inputProps={{ min: 0, max: 24, step: 0.25, style: { textAlign: 'center' } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setOperatori((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </FormSection>

        <Divider sx={{ my: 2 }} />

        {/* 3. Task Svolte (solo se cantiere selezionato) */}
        {selectedCantiere && (
          <>
            <FormSection icon={<CheckCircleIcon color="primary" />} title="Task Svolte">
              {loadingTasks ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : tasks.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Nessuna task trovata per questo cantiere
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Cerca task..."
                    value={searchTask}
                    onChange={(e) => setSearchTask(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Descrizione</TableCell>
                          <TableCell align="center">Qtà Totale</TableCell>
                          <TableCell align="center">Già Completata</TableCell>
                          <TableCell align="center">Completata Oggi</TableCell>
                          <TableCell align="center">Progresso</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTasks.map((task) => {
                          const update = taskUpdates[task.id] || {};
                          const percentuale = task.quantita > 0
                            ? Math.min(100, ((update.quantita_completata || task.quantita_completata || 0) / task.quantita) * 100)
                            : 0;
                          return (
                            <TableRow key={task.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {task.completata && <CheckCircleIcon fontSize="small" color="success" />}
                                  <Typography variant="body2">{task.descrizione}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">{task.quantita}</TableCell>
                              <TableCell align="center">{task.quantita_completata || 0}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={update.quantita_oggi || ''}
                                  onChange={(e) => handleTaskQuantityChange(task.id, e.target.value)}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 100 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={percentuale}
                                    sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                  />
                                  <Typography variant="caption">{Math.round(percentuale)}%</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </FormSection>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* 4. Descrizione Intervento */}
        <FormSection icon={<DescriptionIcon color="primary" />} title="Descrizione Intervento">
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder={tipologia === 'manutenzione' ? 'Descrizione obbligatoria per manutenzioni...' : 'Descrizione intervento...'}
            value={descrizioneIntervento}
            onChange={(e) => setDescrizioneIntervento(e.target.value)}
          />
          {tipologia === 'manutenzione' && !descrizioneIntervento.trim() && (
            <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
              La descrizione è obbligatoria per le manutenzioni
            </Alert>
          )}
        </FormSection>

        <Divider sx={{ my: 2 }} />

        {/* 5. Materiali Utilizzati */}
        <FormSection icon={<BuildIcon color="primary" />} title="Materiali Utilizzati">
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Elenco materiali utilizzati..."
            value={materialiUtilizzati}
            onChange={(e) => setMaterialiUtilizzati(e.target.value)}
          />
        </FormSection>

        <Divider sx={{ my: 2 }} />

        {/* 6. Note */}
        <FormSection icon={<DescriptionIcon color="primary" />} title="Note">
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Note aggiuntive..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormSection>

        <Divider sx={{ my: 2 }} />

        {/* 7. Firma Cliente (solo manutenzione) */}
        {tipologia === 'manutenzione' && (
          <>
            <FormSection icon={<DrawIcon color="primary" />} title="Firma Cliente">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={responsabileAssente}
                    onChange={(e) => setResponsabileAssente(e.target.checked)}
                  />
                }
                label="Responsabile assente"
              />

              {responsabileAssente && (
                <Alert severity="warning" sx={{ mt: 1, mb: 2, borderRadius: 2 }}>
                  <WarningIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Il rapportino verrà salvato come "da firmare"
                </Alert>
              )}

              {!responsabileAssente && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome del Firmatario *"
                    value={nomeFirmatario}
                    onChange={(e) => setNomeFirmatario(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa' }}>
                    <SignatureCanvas
                      ref={sigCanvas}
                      penColor="black"
                      canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'sigCanvas',
                        style: { width: '100%', height: '200px' },
                      }}
                    />
                  </Paper>
                  <Button
                    size="small"
                    onClick={handleClearSignature}
                    sx={{ mt: 1 }}
                  >
                    Pulisci Firma
                  </Button>
                </>
              )}
            </FormSection>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* 8. Allegati */}
        <FormSection icon={<AttachFileIcon color="primary" />} title="Allegati">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            Aggiungi File
          </Button>

          {allegati.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {allegati.map((file, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isImage(file.content_type) ? (
                    <Box
                      component="img"
                      src={file.data_base64}
                      alt={file.filename}
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                    />
                  ) : (
                    <InsertDriveFileIcon color="action" />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" noWrap>{file.filename}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveAllegato(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </FormSection>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            bgcolor: '#e50000',
            '&:hover': { bgcolor: '#c50000' },
            fontWeight: 700,
            borderRadius: 2,
          }}
        >
          {loading ? 'Salvataggio...' : 'Salva Rapportino'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RapportinoStandaloneModal;
